/**
 * 주렁주렁 운영 도구 - 메인 앱
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import StoreSelectPage from './pages/StoreSelectPage';
import ServiceMenuPage from './pages/ServiceMenuPage';
import AlimtalkPage from './pages/AlimtalkPage';
import QrPrintPage from './pages/QrPrintPage';
import EventManagePage from './pages/EventManagePage';
import FamilyManagePage from './pages/FamilyManagePage';
import MembershipRegisterPage from './pages/MembershipRegisterPage';
import QrStickerPage from './pages/QrStickerPage';
import PrinterManagePage from './pages/PrinterManagePage';
import VirtualKioskPage from './pages/VirtualKioskPage';
import QrStickerReaderPage from './pages/QrStickerReaderPage';
import AllInOnePage from './pages/AllInOnePage';
import CouponRedeemPage from './pages/CouponRedeemPage';
import PcCouponOpsPage from './pages/PcCouponOpsPage';
import QuestTagPage from './pages/QuestTagPage';
import QuestManualCheckPage from './pages/QuestManualCheckPage';
import QuestStatusPage from './pages/QuestStatusPage';
import QuestCompletionTestPage from './pages/QuestCompletionTestPage';
import BandQrPrintPage from './pages/BandQrPrintPage';
import ZoolungmanPage from './pages/ZoolungmanPage';
import PassportCancelPage from './pages/PassportCancelPage';
import PassportTestPage from './pages/PassportTestPage';
import OxQuizPreviewPage from './pages/OxQuizPreviewPage';
import BatteryIndicator from './components/BatteryIndicator';
import DevEnvBadge from './components/DevEnvBadge';
import useVersionCheck from './hooks/useVersionCheck';
import useHeartbeat from './hooks/useHeartbeat';
import preloadNoticeFont from './utils/preloadNoticeFont';
import preloadNoticeAssets from './utils/preloadNoticeAssets';
import './App.css';

function OperationalApp() {
  const buildTime = useVersionCheck();
  useHeartbeat();  // 장치관리자 하트비트(설계 §8 / dev-steps S9)

  // 퀘스트 안내판 자원(폰트 + 배경 이미지)을 앱이 뜨는 즉시 받아 둔다.
  // 🔴 안내판은 **4초뿐**이라 스캔 시점에 받기 시작하면 그 4초가 그대로 지나간다.
  //    · 폰트  → 대체 폰트(Katuri)로 그려져 디자인과 다른 화면이 나간다
  //    · 배경  → `background-color` 만 보인다. 실제 현장 보고: 단풍 안내판이 **붉은 배경만**
  //             뜨고 사라졌다(2026-08-06). 캐시된 뒤엔 정상이라 간헐적으로 보인다.
  //    실측으로 배경은 화면 진입 시점에 요청 0건이었다 — 폰트만 대비돼 있던 구멍이다.
  // 둘은 같은 이유·같은 시점이라 **한 자리에서 함께** 부른다. 한쪽만 남기지 말 것.
  // 새로고침(version.json 갱신)마다 다시 실행되지만 브라우저 캐시가 재다운로드를 막는다.
  useEffect(() => { preloadNoticeFont(); preloadNoticeAssets(); }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/store-select" element={<ProtectedRoute allowWithoutStore><StoreSelectPage /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><ServiceMenuPage /></ProtectedRoute>} />
            <Route path="/alimtalk" element={<ProtectedRoute><AlimtalkPage /></ProtectedRoute>} />
            <Route path="/qrprint" element={<ProtectedRoute><QrPrintPage /></ProtectedRoute>} />
            <Route path="/qr-sticker" element={<ProtectedRoute><QrStickerPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventManagePage /></ProtectedRoute>} />
            <Route path="/family" element={<ProtectedRoute><FamilyManagePage /></ProtectedRoute>} />
            <Route path="/membership" element={<ProtectedRoute><MembershipRegisterPage /></ProtectedRoute>} />
            <Route path="/printer-manage" element={<ProtectedRoute><PrinterManagePage /></ProtectedRoute>} />
            <Route path="/virtual-kiosk" element={<ProtectedRoute><VirtualKioskPage /></ProtectedRoute>} />
            <Route path="/qr-auto" element={<ProtectedRoute><QrStickerReaderPage /></ProtectedRoute>} />
            <Route path="/all" element={<ProtectedRoute><AllInOnePage /></ProtectedRoute>} />
            <Route path="/coupon-redeem" element={<ProtectedRoute><CouponRedeemPage /></ProtectedRoute>} />
            <Route path="/pos-coupon-ops" element={<ProtectedRoute><PcCouponOpsPage /></ProtectedRoute>} />
            <Route path="/quest-tag" element={<ProtectedRoute><QuestTagPage /></ProtectedRoute>} />
            <Route path="/quest-manual-check" element={<ProtectedRoute><QuestManualCheckPage /></ProtectedRoute>} />
            <Route path="/quest-status" element={<ProtectedRoute><QuestStatusPage /></ProtectedRoute>} />
            <Route path="/band-qr-print" element={<ProtectedRoute><BandQrPrintPage /></ProtectedRoute>} />
            {/* 주렁맨 — 지급 API 가 STAFF 전용이라 라우트도 STAFF 로 막는다(dev-steps §S5) */}
            <Route path="/zoolungman" element={<ProtectedRoute minRole="STAFF"><ZoolungmanPage /></ProtectedRoute>} />
            {/* 여권취소 — 조회·취소 API 가 STAFF 전용이므로(D5) 라우트도 STAFF 로 막는다.
                🔴 카드만 숨기면 URL 직접 입력으로 들어와 취소에서만 403 이 나는 dead-end 가 남는다(S17/E-15). */}
            <Route path="/passport-cancel" element={<ProtectedRoute minRole="STAFF"><PassportCancelPage /></ProtectedRoute>} />
            {/* 여권테스트 — 발급·인쇄·목록 3종 API 가 STAFF 전용이므로 라우트도 STAFF 로 막는다.
                🔴 카드만 숨기면 URL 직접 입력으로 들어와 발급에서만 403 이 나는 dead-end 가 남는다.
                서버는 TEST 서버가 아니면 403 TEST_SERVER_ONLY 로 한 번 더 막는다(최종 방어). */}
            <Route path="/passport-test" element={<ProtectedRoute minRole="STAFF"><PassportTestPage /></ProtectedRoute>} />
            {/* 미션완료테스트 — 임의 상태 편집 API와 동일하게 STAFF + TEST 서버 이중 게이트. */}
            <Route path="/quest-completion-test" element={<ProtectedRoute minRole="STAFF"><QuestCompletionTestPage /></ProtectedRoute>} />
          </Routes>
          {buildTime && <div className="build-version">v.{buildTime}</div>}
          <DevEnvBadge />
          <BatteryIndicator />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

function App() {
  // CRM iframe 미리보기는 현장 장치가 아니다. 인증 복원·하트비트·배터리·버전 체크를
  // 시작하지 않는 독립 경로로 먼저 분리해 실제 퀘스트/보상 상태에 부작용이 없게 한다.
  if (window.location.pathname === '/preview/ox-quiz') {
    return <OxQuizPreviewPage />;
  }
  return <OperationalApp />;
}

export default App;
